import { FC, useCallback, useMemo, useState } from 'react'
import moment from 'moment'

// BL
import { UserDocument } from '@/features/user/your-practice/documents/lib/userDocuments.slice'
import {
  groupDocumentsByYear,
  groupDocumentsByMonthWithinYear,
  sortDocuments,
} from '@/features/user/your-practice/documents/lib/helpers'

// UI
import { Table, Text, Icon } from '@/ui'
import { TypographyStyles } from '@/styles'
import {
  getUserDocumentCategoriesById,
  selectUserDocumentCategoryList,
} from '@/features/admin/user-document-categories/userDocumentCategories.selectors'
import { useReselector } from '@/utils/sharedHooks'
import { DATE_FORMATS } from '@/utils/dateHelpers'
import { selectTaxUserDocuments } from '@/features/taxes/annual-taxes/taxUserDocuments.selector'
import { RowComponentProps } from '@/features/user/your-practice/documents/table/TableRows'

import {
  faSortUp,
  faSortDown,
  faSort,
  faCaretDown,
  faCaretRight,
} from '@fortawesome/pro-solid-svg-icons'
import { GroupByEnum } from './helpers'

export interface TableHeaderConfig {
  sortBy: string | null
  title: string
  width?: string | number
}

interface UserDocumentsTableHeaderProps {
  columns: TableHeaderConfig[]
  type: 'Primary' | 'Secondary' | 'Tertiary'
  onClick?: () => void
  onSort?: (sortBy: string | null) => void
  isOpen?: boolean
  sortVal?: string | null
  sortAsc?: boolean
}

export interface TableOptions {
  sortFn: (_a: UserDocument, _b: UserDocument) => number
  groupBy: GroupByEnum
}

interface UserDocumentsTableProps {
  columns: TableHeaderConfig[]
  documents: UserDocument[]
  RowComponent: FC<RowComponentProps>
  tableOptions: TableOptions
  onCheck?: (id: number, checked: boolean) => void
  fixed?: boolean
}

const useDocSort = (documents: UserDocument[]) => {
  const categories = useReselector(selectUserDocumentCategoryList)
  const [sortVal, setSortVal] = useState<string | null>(null)
  const [sortAsc, setSortAsc] = useState(false)

  const sort = useCallback(
    (value: string | null) => {
      if (value && sortVal === value) {
        setSortAsc((sortAsc) => !sortAsc)
      } else {
        setSortVal(value)
        setSortAsc(false)
      }
    },
    [sortVal]
  )

  const sortedDocs = useMemo(
    () =>
      sortDocuments({
        docs: documents,
        sortAsc,
        sortByVal: sortVal,
        cats: categories,
      }),
    [categories, documents, sortAsc, sortVal]
  )

  return { sort, sortedDocs, sortVal, sortAsc }
}

const EMPTY_COLUMN = {
  sortBy: null,
  title: '',
}

const SortIcon = ({
  type,
  isOpen,
  sortVal,
  sortAsc,
  index,
  sortBy,
}: {
  type: 'Primary' | 'Secondary' | 'Tertiary'
  isOpen?: boolean
  sortVal?: string | null
  sortAsc?: boolean
  index: number
  sortBy: string | null
}) => {
  let icon
  if (type === 'Tertiary') {
    if (sortBy) {
      if (sortBy === sortVal) {
        icon = sortAsc ? faSortUp : faSortDown
      } else {
        icon = faSort
      }
    }
  } else if (!index) {
    icon = isOpen ? faCaretDown : faCaretRight
  }

  return (icon && <Icon icon={icon} className="pl-2" size={'sm'} />) || null
}

const renderRowsForGroup = ({
  group,
  RowComponent,
  onCheck,
}: {
  group: UserDocument[]
  RowComponent: FC<RowComponentProps>
  onCheck?: (id: number, checked: boolean) => void
}) =>
  group.map((doc) => (
    <RowComponent
      // rows with an "Upload" action button all have -1 as the doc Id, which messes up the sorting
      key={`group-row-${doc.id}-${doc.userDocumentCategoryId}`}
      document={doc}
      onCheck={onCheck}
    />
  ))

const renderMonthRow = ({
  groups,
  ...rest
}: {
  groups: Map<number, UserDocument[]>
  RowComponent: FC<RowComponentProps>
  columns: TableHeaderConfig[]
  onCheck?: (id: number, checked: boolean) => void
}) =>
  [...groups.keys()].map((month) => (
    <MonthRow
      key={`document-table-group-${month}`}
      groups={groups}
      month={month}
      {...rest}
    />
  ))

const UserDocumentsTableHeader = ({
  onSort = () => {},
  onClick = () => {},
  columns,
  type,
  isOpen,
  sortVal,
  sortAsc,
}: UserDocumentsTableHeaderProps) => {
  return (
    <Table.Header
      className={`user-documents-table-header ${type.toLowerCase()}`}
      onClick={onClick}
    >
      <Table.Row>
        {columns.map(({ title, sortBy, width }, i) => (
          <Table.HeaderCell
            key={`table-header-${i}`}
            onClick={() => onSort(sortBy)}
            width={width}
          >
            {title}
            <SortIcon
              type={type}
              sortBy={sortBy}
              sortVal={sortVal}
              sortAsc={sortAsc}
              isOpen={isOpen}
              index={i}
            />
          </Table.HeaderCell>
        ))}
      </Table.Row>
    </Table.Header>
  )
}

const MonthRow = ({
  columns,
  groups,
  RowComponent,
  month,
  onCheck,
}: {
  columns: TableHeaderConfig[]
  groups: Map<number, UserDocument[]>
  RowComponent: FC<RowComponentProps>
  month: number
  onCheck?: (id: number, checked: boolean) => void
}) => {
  const monthName = moment().month(month).format(DATE_FORMATS.MONTH_LONG)
  const [open, setOpen] = useState(true)
  const { sort, sortedDocs, sortVal, sortAsc } = useDocSort(
    groups.get(month) || []
  )

  return (
    <>
      <UserDocumentsTableHeader
        type="Secondary"
        columns={[
          { title: monthName, sortBy: null },
          ...columns
            .slice(1)
            .map((col) => ({ ...EMPTY_COLUMN, width: col.width })),
        ]}
        onClick={() => setOpen((open) => !open)}
        isOpen={open}
      />
      {open && (
        <>
          <UserDocumentsTableHeader
            type="Tertiary"
            columns={columns}
            onSort={(value) => sort(value)}
            sortAsc={sortAsc}
            sortVal={sortVal}
          />
          <Table.Body>
            {renderRowsForGroup({
              group: sortedDocs,
              RowComponent,
              onCheck,
            })}
          </Table.Body>
        </>
      )}

      <Table.Row className="spacer" />
    </>
  )
}

const GroupedByYearTable = ({
  columns,
  year,
  groups,
  RowComponent,
  onCheck,
}: {
  columns: TableHeaderConfig[]
  year: number
  groups: Map<number, UserDocument[]>
  RowComponent: FC<RowComponentProps>
  onCheck?: (id: number, checked: boolean) => void
}) => {
  const [open, setOpen] = useState(true)
  const { sort, sortedDocs, sortVal, sortAsc } = useDocSort(
    groups.get(year) || []
  )

  return (
    <>
      <UserDocumentsTableHeader
        type="Primary"
        columns={[
          { title: year.toString(), sortBy: null, width: columns[0].width },
          ...columns
            .slice(1)
            .map((col) => ({ ...EMPTY_COLUMN, width: col.width })),
        ]}
        onClick={() => setOpen((open) => !open)}
        isOpen={open}
      />
      {open && (
        <>
          <UserDocumentsTableHeader
            type="Tertiary"
            columns={columns}
            onSort={(value) => sort(value)}
            sortAsc={sortAsc}
            sortVal={sortVal}
          />
          <Table.Body>
            {renderRowsForGroup({
              group: sortedDocs,
              RowComponent,
              onCheck,
            })}
          </Table.Body>
        </>
      )}
      <Table.Row className="spacer" />
    </>
  )
}

const GroupMonthWithinYear = ({
  columns,
  groups,
  RowComponent,
  year,
  onCheck,
}: {
  columns: TableHeaderConfig[]
  groups: Map<number, Map<number, UserDocument[]>>
  RowComponent: FC<RowComponentProps>
  year: number
  onCheck?: (id: number, checked: boolean) => void
}) => {
  const yearGroup = groups.get(year)
  const [open, setOpen] = useState(true)

  if (!yearGroup) {
    return null
  }

  return (
    <>
      <UserDocumentsTableHeader
        type="Primary"
        columns={[
          { title: year.toString(), sortBy: null },
          ...columns
            .slice(1)
            .map((col) => ({ ...EMPTY_COLUMN, width: col.width })),
        ]}
        onClick={() => setOpen((open) => !open)}
        isOpen={open}
      />
      {open &&
        renderMonthRow({ groups: yearGroup, RowComponent, columns, onCheck })}
    </>
  )
}

const NoGrouping = ({
  documents,
  columns,
  RowComponent,
  onCheck,
}: {
  documents: UserDocument[]
  columns: Array<{ sortBy: string | null; title: string }>
  RowComponent: FC<RowComponentProps>
  onCheck?: (id: number, checked: boolean) => void
}) => {
  const { sort, sortedDocs, sortVal, sortAsc } = useDocSort(documents)

  return (
    <>
      {documents.length > 0 && (
        <UserDocumentsTableHeader
          type="Tertiary"
          columns={columns}
          onSort={(value) => sort(value)}
          sortAsc={sortAsc}
          sortVal={sortVal}
        />
      )}
      <Table.Body>
        {renderRowsForGroup({
          group: sortedDocs,
          RowComponent,
          onCheck,
        })}
      </Table.Body>
    </>
  )
}

const AdminUserDocumentsTable = ({
  columns,
  documents,
  RowComponent,
  tableOptions,
  onCheck,
}: UserDocumentsTableProps) => {
  const sortedDocuments = useMemo(
    () => documents.sort(tableOptions.sortFn),
    [documents, tableOptions.sortFn]
  )
  const documentCategories = useReselector(getUserDocumentCategoriesById)
  const taxUserDocuments = useReselector(selectTaxUserDocuments)
  const tableContent = useMemo(() => {
    switch (tableOptions.groupBy) {
      case GroupByEnum.StatementYear:
      case GroupByEnum.CategoryYear:
      case GroupByEnum.CreatedAtYear: {
        const groups = groupDocumentsByYear(
          sortedDocuments,
          tableOptions.groupBy as GroupByEnum,
          documentCategories,
          taxUserDocuments
        )

        return [...groups.keys()]
          .sort((a, b) => b - a)
          .map((year) => (
            <GroupedByYearTable
              key={`document-table-group-${year}`}
              groups={groups}
              year={year}
              columns={columns}
              RowComponent={RowComponent}
              onCheck={onCheck}
            />
          ))
      }
      case GroupByEnum.MonthWithinYear: {
        const groups = groupDocumentsByMonthWithinYear(sortedDocuments)

        return [...groups.keys()]
          .sort((a, b) => b - a)
          .map((year) => (
            <GroupMonthWithinYear
              key={`document-table-group-${year}`}
              year={year}
              RowComponent={RowComponent}
              columns={columns}
              groups={groups}
              onCheck={onCheck}
            />
          ))
      }
      case GroupByEnum.None:
        return (
          <NoGrouping
            documents={sortedDocuments}
            columns={columns}
            RowComponent={RowComponent}
            onCheck={onCheck}
          />
        )
      default:
        return tableOptions.groupBy satisfies never
    }
  }, [
    RowComponent,
    columns,
    documentCategories,
    onCheck,
    sortedDocuments,
    tableOptions.groupBy,
    taxUserDocuments,
  ])

  if (sortedDocuments.length === 0) {
    return (
      <div className="centered">
        <br />
        <br />
        <Text variant={TypographyStyles.BODY_SM}>No Documents Yet!</Text>
      </div>
    )
  }
  return (
    <Table id="user-documents-table" compact="very">
      {tableContent}
    </Table>
  )
}

export default AdminUserDocumentsTable
